import type React from "react";
import { Box, Paper, Typography, alpha } from "@mui/material";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import { formatRupiah } from "../../utils/formatRupiah";

interface FinancialSummaryCardsProps {
  totalPaid: number;
  totalVerified: number;
  difference: number;
  totalPaidLabel: string;
  totalVerifiedLabel: string;
  differenceLabel: string;
  totalPaidTestId?: string;
  totalVerifiedTestId?: string;
  differenceTestId?: string;
  /** When set, middle card shows this instead of the formatted totalVerified amount. */
  totalVerifiedDisplay?: React.ReactNode;
  /** When set, difference card shows this instead of the formatted difference amount. */
  differenceDisplay?: React.ReactNode;
  totalPaidFooter?: React.ReactNode;
  totalVerifiedFooter?: React.ReactNode;
  differenceFooter?: React.ReactNode;
  orientation?: "horizontal" | "vertical";
  sx?: object;
}

const iconBoxSx = (bgColor: string, color: string) => ({
  width: 40,
  height: 40,
  borderRadius: 1.5,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  bgcolor: bgColor,
  color,
  flexShrink: 0,
});

const pendingValueSx = {
  fontStyle: "italic",
  color: "text.secondary",
  fontWeight: 500,
} as const;

const FinancialSummaryCards: React.FC<FinancialSummaryCardsProps> = ({
  totalPaid,
  totalVerified,
  difference,
  totalPaidLabel,
  totalVerifiedLabel,
  differenceLabel,
  totalPaidTestId,
  totalVerifiedTestId,
  differenceTestId,
  totalVerifiedDisplay,
  differenceDisplay,
  totalPaidFooter,
  totalVerifiedFooter,
  differenceFooter,
  orientation = "horizontal",
  sx,
}) => {
  const differenceIsZero = difference === 0;
  const VarianceIcon = differenceIsZero ? TrendingFlatIcon : TrendingDownIcon;
  const isVertical = orientation === "vertical";
  const verifiedValue = totalVerifiedDisplay ?? formatRupiah(totalVerified);
  const differenceValue = differenceDisplay ?? formatRupiah(difference);
  const differenceColor = differenceIsZero ? "#22C55E" : "#EF4444";
  const differenceIconColor = differenceColor;

  return (
    <Box
      sx={{
        display: "grid",
        gap: 2,
        gridTemplateColumns: isVertical ? "1fr" : { xs: "1fr", sm: "repeat(3, 1fr)" },
        mb: isVertical ? 0 : 3,
        height: isVertical ? "100%" : "auto",
        alignContent: isVertical ? "start" : undefined,
        ...sx,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: 2,
          border: 1,
          borderColor: "divider",
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
        }}
      >
        <Box sx={iconBoxSx(alpha("#3B82F6", 0.12), "#3B82F6")}>
          <AccountBalanceWalletOutlinedIcon fontSize="small" />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            {totalPaidLabel}
          </Typography>
          <Typography
            variant="h6"
            component="p"
            data-testid={totalPaidTestId}
            sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums", lineHeight: 1.2 }}
          >
            {formatRupiah(totalPaid)}
          </Typography>
          {totalPaidFooter}
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: 2,
          border: 1,
          borderColor: "divider",
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
        }}
      >
        <Box sx={iconBoxSx(alpha("#22C55E", 0.12), "#22C55E")}>
          <CheckCircleOutlineIcon fontSize="small" />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            {totalVerifiedLabel}
          </Typography>
          <Typography
            variant="h6"
            component="p"
            data-testid={totalVerifiedTestId}
            sx={{
              fontVariantNumeric: "tabular-nums",
              lineHeight: 1.2,
              ...(totalVerifiedDisplay ? pendingValueSx : { fontWeight: 700 }),
            }}
          >
            {verifiedValue}
          </Typography>
          {totalVerifiedFooter}
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: 2,
          border: 1,
          borderColor: "divider",
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
        }}
      >
        <Box
          sx={iconBoxSx(
            differenceDisplay ? alpha("#94A3B8", 0.16) : alpha(differenceIconColor, 0.12),
            differenceDisplay ? "#64748B" : differenceIconColor,
          )}
        >
          <VarianceIcon fontSize="small" />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            {differenceLabel}
          </Typography>
          <Typography
            variant="h6"
            component="p"
            data-testid={differenceTestId}
            sx={{
              fontVariantNumeric: "tabular-nums",
              lineHeight: 1.2,
              ...(differenceDisplay ? pendingValueSx : { fontWeight: 700, color: differenceColor }),
            }}
          >
            {differenceValue}
          </Typography>
          {differenceFooter}
        </Box>
      </Paper>
    </Box>
  );
};

export default FinancialSummaryCards;
